function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const shimmer =
  'relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.5s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/60 before:to-transparent dark:before:via-slate-600/40';

export function SkeletonBlock({ className = '' }) {
  return (
    <div
      className={classNames(
        shimmer,
        'rounded-md bg-slate-200 dark:bg-slate-700',
        className,
      )}
    />
  );
}

export function MenuCardSkeleton() {
  return (
    <div className="flex min-h-[4.85rem] max-w-[13rem] min-w-[13rem] items-center rounded-md border border-slate-200 bg-white px-3 py-2.5 dark:border-slate-700 dark:bg-slate-800">
      <div className="flex-1 space-y-2 pr-2">
        <SkeletonBlock className="h-4 w-24" />
        <SkeletonBlock className="h-3 w-16" />
      </div>
      <SkeletonBlock className="h-5 w-5 rounded-full" />
    </div>
  );
}

export function MenuListSkeleton({ count = 3 }) {
  return (
    <div className="flex items-center gap-2 py-1 pr-1">
      {Array.from({ length: count }).map((_, i) => (
        <MenuCardSkeleton key={i} />
      ))}
    </div>
  );
}

export function CategoryCardSkeleton() {
  return (
    <div className="mx-3 my-2 flex min-h-[5.25rem] items-center rounded-md border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
      <div className="flex-1 space-y-2">
        <SkeletonBlock className="h-4 w-28" />
        <SkeletonBlock className="h-3 w-40" />
      </div>
      <SkeletonBlock className="h-5 w-5 rounded-full" />
    </div>
  );
}

export function CategoryListSkeleton({ count = 4 }) {
  return (
    <div>
      {Array.from({ length: count }).map((_, i) => (
        <CategoryCardSkeleton key={i} />
      ))}
    </div>
  );
}

export function ProductCardSkeleton() {
  return (
    <div className="mx-3 my-2 flex min-h-[5.75rem] items-center gap-4 rounded-md border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
      <SkeletonBlock className="h-14 w-14 min-h-[3.5rem] min-w-[3.5rem] !rounded-full" />
      <div className="flex-1 min-w-0 space-y-2">
        <SkeletonBlock className="h-4 w-32" />
        <SkeletonBlock className="h-3 w-48" />
        <div className="flex gap-1">
          <SkeletonBlock className="h-4 w-4 rounded" />
          <SkeletonBlock className="h-4 w-4 rounded" />
        </div>
      </div>
      <SkeletonBlock className="h-5 w-10 rounded-full" />
      <SkeletonBlock className="h-5 w-5 rounded-full" />
    </div>
  );
}

export function ProductListSkeleton({ count = 4 }) {
  return (
    <div>
      {Array.from({ length: count }).map((_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
    </div>
  );
}

export function ItemListSkeleton({ count = 3 }) {
  return (
    <div className="px-3 pb-3 pt-2">
      <div className="mb-3 rounded-md border border-slate-200 bg-gradient-to-r from-white to-slate-50 px-4 py-3 dark:border-slate-700 dark:from-slate-800 dark:to-slate-800/80">
        <SkeletonBlock className="h-2.5 w-16 mb-2" />
        <SkeletonBlock className="h-5 w-28 mb-1.5" />
        <SkeletonBlock className="h-3 w-52" />
      </div>
      <div className="overflow-hidden rounded-md border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
        <div className="px-6 py-3 border-b border-slate-200 dark:border-slate-700 flex gap-16">
          <SkeletonBlock className="h-3.5 w-12" />
          <SkeletonBlock className="h-3.5 w-10" />
        </div>
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            className="flex items-center justify-between px-6 py-4 border-b border-slate-100 last:border-0 dark:border-slate-700"
          >
            <SkeletonBlock className="h-4 w-28" />
            <SkeletonBlock className="h-4 w-16" />
            <SkeletonBlock className="h-5 w-5 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function OptionGroupCardSkeleton() {
  return (
    <div className="mx-3 my-2 flex min-h-[5rem] items-center gap-4 rounded-md border border-slate-200 bg-white px-4 py-2 dark:border-slate-700 dark:bg-slate-800">
      <div className="flex-1 min-w-0 space-y-2">
        <SkeletonBlock className="h-4 w-32" />
        <SkeletonBlock className="h-3 w-24" />
        <SkeletonBlock className="h-3 w-20" />
      </div>
      <SkeletonBlock className="h-5 w-5 rounded-full" />
    </div>
  );
}

export function TableRowsSkeleton({ count = 6, rowHeight = 'h-12' }) {
  return (
    <div className="w-full space-y-2 py-2">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonBlock key={i} className={`${rowHeight} w-full`} />
      ))}
    </div>
  );
}

export function StatGridSkeleton({ count = 4 }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-md border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800 space-y-2"
        >
          <SkeletonBlock className="h-3 w-20" />
          <SkeletonBlock className="h-7 w-28" />
          <SkeletonBlock className="h-3 w-16" />
        </div>
      ))}
    </div>
  );
}

export function OptionGroupListSkeleton({ count = 3 }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white/80 py-2 dark:border-slate-700 dark:bg-slate-800">
      {Array.from({ length: count }).map((_, i) => (
        <OptionGroupCardSkeleton key={i} />
      ))}
    </div>
  );
}
